import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useDispatch, useSelector } from "react-redux";
import { clearCartNotice } from "../../../store/slices/CartSlice/CartSlice";

// This popup is meant to read as a warning (like a caution sign), not a
// CSFA-themed element, so it uses a fixed amber instead of CSFA's own
// site-configurable --primary-color, which can be set to a different color
// per customer in Site Administration.
const WARNING_TEXT = "#78350f"; // Tailwind amber-900, used for the "Quantity now" figure
const WARNING_OUTLINE = "#dc2626"; // Tailwind red-600, used for the header's outline + text on white
// CSA's brand blue (react-interface/src/index.js --company-primary-color for
// MITCO), reused here since that's the blue jford had us match earlier.
const BUTTON_BLUE = "#007dc0";
const BUTTON_BLUE_HOVER = "#009bf2";

/**
 * Popup shown when a product added to the cart was already in it (usually
 * added from another tab or login that this one never saw). The backend adds
 * the quantity to the existing line and sends a cart_notice describing what it
 * did. It stays up until the user acknowledges it, since their cart quantity
 * is not what they just picked.
 *
 * Wording is deliberately generic ("something went wrong") rather than
 * explaining the merge -- jford's call, 2026-09-30 -- but the quantity
 * breakdown stays since that's the part the user actually needs to act on.
 */
export function CartNotice() {
  const dispatch = useDispatch();
  const { cartNotice, cartItems } = useSelector((state) => state.cart);

  if (!cartNotice) return null;

  const { product_code, previous_qty, added_qty, new_qty } = cartNotice;
  const description = cartItems.find(
    (item) => item.product_code === product_code,
  )?.product?.description;
  const close = () => dispatch(clearCartNotice());

  return (
    <Dialog open onClose={close} className="relative z-[10000]">
      <DialogBackdrop className="fixed inset-0 bg-gray-900/60" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-md overflow-hidden rounded-xl bg-white shadow-2xl">
          <div
            className="mx-2 mt-2 flex items-center gap-3 rounded-lg border-2 px-5 py-4"
            style={{ borderColor: WARNING_OUTLINE }}
          >
            <ExclamationTriangleIcon
              className="h-8 w-8 shrink-0"
              style={{ color: WARNING_OUTLINE }}
              aria-hidden="true"
            />
            <DialogTitle
              className="text-lg font-bold"
              style={{ color: WARNING_OUTLINE }}
            >
              Something went wrong on our end
            </DialogTitle>
          </div>

          <div className="px-5 py-4">
            <p className="text-base font-semibold text-gray-900">
              {product_code}
              {description ? ` - ${description}` : ""}
            </p>
            <p className="mt-1 text-sm text-gray-600">
              Please make sure your cart is correct.
            </p>

            <div className="mt-4 flex items-center justify-around rounded-lg border border-gray-200 bg-gray-50 py-3 text-center">
              <div>
                <div className="text-xs uppercase text-gray-500">
                  Already in cart
                </div>
                <div className="text-2xl font-bold text-gray-800">
                  {previous_qty}
                </div>
              </div>
              <div className="text-2xl text-gray-400">+</div>
              <div>
                <div className="text-xs uppercase text-gray-500">Added</div>
                <div className="text-2xl font-bold text-gray-800">
                  {added_qty}
                </div>
              </div>
              <div className="text-2xl text-gray-400">=</div>
              <div>
                <div className="text-xs uppercase text-gray-500">
                  Quantity now
                </div>
                <div
                  className="text-3xl font-extrabold"
                  style={{ color: WARNING_TEXT }}
                >
                  {new_qty}
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end bg-gray-50 px-5 py-3">
            <button
              type="button"
              onClick={close}
              data-autofocus
              className="block rounded-md px-5 py-2 text-center text-sm font-semibold text-white shadow-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
              style={{ backgroundColor: BUTTON_BLUE }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = BUTTON_BLUE_HOVER;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = BUTTON_BLUE;
              }}
            >
              OK, got it
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}

export default CartNotice;
